import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { allChatTypeOptions, chatTypeOptions } from '../../helpers/MonitorTypeHelpers';
import FilterBox from '../../components/filter-box/FilterBox';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/account/AccountContexts';
import DropdownItem from '../../components/dropdown-item/DropdownItem';
import { useFilters } from '../../contexts/filters/FiltersContext';
import { PRICING } from '../../routes/path';
import { getPeriodParam } from '../../helpers/filters/FiltersHelper';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { subDays, subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { FilterItem } from '../../components/filter-box/models/FilterBoxModel';
import { MonitorPeriod } from '../../types/monitor/MonitorPeriod';
import { StandardPeriodType } from '../../types/filters/StandardPeriodType';
import { ChatType } from '../../types/monitor/ChatType';
import { Checkbox } from 'primereact/checkbox';
import './FiltersPanel.scss';

const buildUrlWithModels = (
  basePath: string,
  period: string,
  selectedTypes: ChatType[],
  allAvailable: ChatType[],
  currentSearch: string
): string => {
  const parts = basePath.split('/').filter(Boolean);
  const newParts = parts.slice(0, -2);
  const path = `/${newParts.join('/')}/${period}/all`;

  const params = new URLSearchParams(currentSearch);
  if (selectedTypes.length >= allAvailable.length) {
    params.delete('models');
  } else {
    params.set('models', selectedTypes.map((t) => t.toLowerCase()).join(','));
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

interface FilterProps {
  baseUrl: string;
  showDateRange?: boolean;
  showChatTypeDropdown?: boolean;
  showPeriodTabs?: boolean;
  children?: ReactNode;
}

const FilterPanel: React.FC<FilterProps> = ({
  baseUrl,
  showDateRange = true,
  showChatTypeDropdown = true,
  showPeriodTabs = true,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const {
    monitorPeriod,
    selectedChatType,
    selectedChatTypes,
    range,
    setRange,
    dateRange,
    selectedPeriod: selectedPeriodContext,
    setSelectedPeriod: setSelectedPeriodContext,
  } = useFilters();
  const [selectedPeriod, setSelectedPeriod] = useState<StandardPeriodType>(
    selectedPeriodContext || 'LAST_30_DAYS'
  );
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedPeriodContext) {
      setSelectedPeriod(selectedPeriodContext);
    }
  }, [selectedPeriodContext]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
    };

    if (isModelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModelDropdownOpen]);

  const availableOptions = useMemo(() => {
    if (!accountResponse) return [];
    return chatTypeOptions(accountResponse.features).filter((opt) => opt.type !== 'ALL');
  }, [accountResponse]);

  const allOptions = useMemo(() => {
    if (!accountResponse) return [];
    return allChatTypeOptions(accountResponse.features).filter(
      (opt) => opt.type !== 'ALL' && !opt.separator
    );
  }, [accountResponse]);

  const availableTypes = useMemo(() => availableOptions.map((opt) => opt.type), [availableOptions]);

  const isAllSelected = useMemo(
    () => selectedChatTypes.length >= availableTypes.length,
    [selectedChatTypes, availableTypes]
  );

  const standardPeriodOptions = useMemo(
    () => [
      { label: t('last7Days'), value: 'LAST_7_DAYS' },
      { label: t('last14Days'), value: 'LAST_14_DAYS' },
      { label: t('last30Days'), value: 'LAST_30_DAYS' },
      { label: t('last3Months'), value: 'LAST_3_MONTHS' },
      { label: t('customPeriod'), value: 'CUSTOM' },
    ],
    [t]
  );

  const calculateStandardRange = useCallback((period: string): Date[] | null => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate: Date;
    let endDate: Date = today;

    switch (period as StandardPeriodType) {
      case 'LAST_7_DAYS':
        startDate = subDays(today, 6);
        break;
      case 'LAST_14_DAYS':
        startDate = subDays(today, 13);
        break;
      case 'LAST_30_DAYS':
        startDate = subDays(today, 29);
        break;
      case 'LAST_3_MONTHS':
        startDate = subMonths(today, 3);
        break;
      case 'CUSTOM':
        return null;
      default:
        return null;
    }

    return [startDate, endDate];
  }, []);

  const handleDateRangeChange = (newRange: Date[]): void => {
    setRange(newRange);
  };

  const handleModelToggle = (type: ChatType) => {
    if (!monitorPeriod) return;

    const isSelected = selectedChatTypes.includes(type);
    let newTypes: ChatType[];

    if (isSelected) {
      if (selectedChatTypes.length <= 1) return;
      newTypes = selectedChatTypes.filter((t) => t !== type);
    } else {
      newTypes = [...selectedChatTypes, type];
    }

    navigate(buildUrlWithModels(baseUrl, getPeriodParam(monitorPeriod), newTypes, availableTypes, location.search));
  };

  const handleSelectAll = () => {
    if (!monitorPeriod) return;

    const newTypes = isAllSelected ? [availableTypes[0]] : [...availableTypes];
    navigate(buildUrlWithModels(baseUrl, getPeriodParam(monitorPeriod), newTypes, availableTypes, location.search));
  };

  // Quick-select a single model — isolates it without manually unchecking the rest.
  const handleSelectOnly = (type: ChatType) => {
    if (!monitorPeriod) return;

    navigate(buildUrlWithModels(baseUrl, getPeriodParam(monitorPeriod), [type], availableTypes, location.search));
    setIsModelDropdownOpen(false);
  };

  const selectedLabel = useMemo(() => {
    if (isAllSelected) return t('all');
    if (selectedChatTypes.length === 1) {
      const opt = availableOptions.find((o) => o.type === selectedChatTypes[0]);
      return opt?.name || t('all');
    }
    return `${selectedChatTypes.length} ${t('modelsSelected')}`;
  }, [isAllSelected, selectedChatTypes, availableOptions, t]);

  const selectedIcon = useMemo(() => {
    if (isAllSelected) return 'ico--models ico--globe';
    if (selectedChatTypes.length === 1) {
      const opt = availableOptions.find((o) => o.type === selectedChatTypes[0]);
      return opt?.icon || 'ico--models ico--globe';
    }
    return 'ico--models ico--globe';
  }, [isAllSelected, selectedChatTypes, availableOptions]);

  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!selectedChatType || !monitorPeriod) {
      return [];
    }

    const periods: { period: string; label: string; monitorPeriod: MonitorPeriod }[] = [
      { period: 'daily', label: t('dailyFilter'), monitorPeriod: 'DAILY' },
      { period: 'weekly', label: t('weeklyFilter'), monitorPeriod: 'WEEKLY' },
      { period: 'monthly', label: t('monthlyFilter'), monitorPeriod: 'MONTHLY' },
    ];

    return periods.map(({ period, label, monitorPeriod: periodType }) => ({
      url: buildUrlWithModels(baseUrl, period, selectedChatTypes, availableTypes, location.search),
      label,
      isActive: monitorPeriod === periodType,
    }));
  }, [baseUrl, selectedChatType, selectedChatTypes, monitorPeriod, availableTypes, location.search, t]);

  if (!accountResponse || !monitorPeriod || !selectedChatType || !range) {
    return <></>;
  }

  return (
    <div className="filters-panel">
      {children}
      {showChatTypeDropdown && (
        <div className="model-select" ref={dropdownRef}>
          <div
            className={`model-select__trigger ${
              isModelDropdownOpen ? 'model-select__trigger--open' : ''
            }`}
            onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
          >
            <DropdownItem icon={selectedIcon} name={selectedLabel} />
            <div className="model-select__chevron">
              <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                <path d="M7 10L1.5 4h11L7 10z" fill="currentColor" />
              </svg>
            </div>
          </div>
          {isModelDropdownOpen && (
            <div className="model-select__panel">
              <ul>
                <li
                  className={`model-select__item ${isAllSelected ? 'model-select__item--selected' : ''}`}
                  onClick={handleSelectAll}
                >
                  <Checkbox checked={isAllSelected} />
                  <DropdownItem icon="ico--models ico--globe" name={t('all')} />
                </li>
                {allOptions.map((option) => {
                  const isAvailable = availableTypes.includes(option.type);
                  const isChecked = selectedChatTypes.includes(option.type);
                  const isAiMode = option.type === 'AI_MODE_BROWSER';
                  const canManage = accountResponse.role === 'OWNER' || accountResponse.role === 'ADMIN';
                  const lockedTooltip = option.locked
                    ? canManage
                      ? 'upgradeToAccess'
                      : 'upgradeToAccessContact'
                    : undefined;

                  if (!isAvailable && !option.locked) return null;

                  return (
                    <li
                      key={option.type}
                      className={`model-select__item ${
                        isChecked ? 'model-select__item--selected' : ''
                      } ${option.locked ? 'model-select__item--locked' : ''}`}
                      onClick={() => {
                        if (!option.locked) handleModelToggle(option.type);
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={!!option.locked || (isChecked && selectedChatTypes.length <= 1)}
                      />
                      <DropdownItem
                        icon={option.icon}
                        name={option.name}
                        tooltipRef={option.type}
                        tooltipText={lockedTooltip}
                        tooltipLink={option.locked && canManage ? PRICING : undefined}
                        isDisabled={!!option.locked}
                        isNew={!option.locked && isAiMode}
                      />
                      {isAvailable &&
                        !option.locked &&
                        !(isChecked && selectedChatTypes.length === 1) && (
                          <button
                            type="button"
                            className="model-select__only"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectOnly(option.type);
                            }}
                          >
                            {t('modelSelectOnly')}
                          </button>
                        )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
      {showDateRange && (
        <FilterBox
          items={showPeriodTabs ? periodFilterItems : []}
          children={
            <DateRangePicker
              value={range}
              onChange={handleDateRangeChange}
              selectedPeriod={selectedPeriod}
              onPeriodChange={(period) => {
                const periodValue = period as StandardPeriodType;
                setSelectedPeriod(periodValue);
                if (setSelectedPeriodContext) {
                  setSelectedPeriodContext(periodValue);
                }
              }}
              minDate={dateRange?.oldestDate}
              maxDate={dateRange?.newestDate}
              periodOptions={standardPeriodOptions}
              calculateRange={calculateStandardRange}
              defaultPeriod={selectedPeriodContext || 'LAST_30_DAYS'}
              oldestDate={dateRange?.oldestDate}
            />
          }
        />
      )}
    </div>
  );
};

export default FilterPanel;
